import React, { useState, useEffect } from 'react';
import api from '../../../api';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.css';
import { getRoundRobinBaseUrl } from '../../../loadBalancer';
import { useAuth } from '../../Layouts/context/Authcontext';

const HorizontalFilterBar = ({ filters: initialFilters = {}, onFilterChange }) => {
  const defaultFilters = {
    client: '',
    scope: '',
    user: '',
    startDate: '',
    endDate: '',
    monthYear: '',
  };

  const mergedInitial = { ...defaultFilters, ...initialFilters };
  const [filters, setFilters] = useState(mergedInitial);
  const [clientList, setClientList] = useState([]);
  const [scopeList, setScopeList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const { accessToken } = useAuth();

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await api.get(`${getRoundRobinBaseUrl()}/uploadedFile/getClient`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setClientList(res.data.data.map((c) => ({ label: c.client, value: c._id })));
      } catch (err) {
        console.error('Error fetching clients:', err);
      }
    };
    fetchClients();
  }, [accessToken]);

  useEffect(() => {
    if (filters.client) {
      const fetchScopes = async () => {
        try {
          const res = await api.get(`${getRoundRobinBaseUrl()}/uploadedFile/getScopebasedOnClient?clientId=${filters.client}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          setScopeList(res.data.data.map((s) => ({ label: s.displayName, value: s._id })));
        } catch (err) {
          console.error('Error fetching scopes:', err);
        }
      };
      fetchScopes();
    } else {
      setScopeList([]);
    }
  }, [filters.client, accessToken]);

  useEffect(() => {
    if (filters.scope) {
      const fetchEmployees = async () => {
        try {
          const res = await api.get(`${getRoundRobinBaseUrl()}/uploadedFile/getEmployeeOnScope?scopeId=${filters.scope}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          setEmployeeList(res.data.data.map((e) => ({ label: e.FirstName, value: e.employeeID })));
        } catch (err) {
          console.error('Error fetching employees:', err);
        }
      };
      fetchEmployees();
    } else {
      setEmployeeList([]);
    }
  }, [filters.scope, accessToken]);

  const formatToMDY = (d) => {
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  const triggerFilterChange = (updated) => {
    const payload = {
      ...updated,
      startDate: updated.startDate ? formatToMDY(new Date(updated.startDate)) : '',
      endDate: updated.endDate ? formatToMDY(new Date(updated.endDate)) : '',
    };

    if (!updated.monthYear) delete payload.monthYear;

    if (!isEqual(filters, updated)) {
      onFilterChange && onFilterChange(payload);
    }
  };

const handleChange = (e) => {
  const { name, value } = e.target;
  const updated = {
    ...filters,
    [name]: value,
  };

  if (name === 'client') {
    updated.user = '';
    setScopeList([]);
    setEmployeeList([]);
  } else if (name === 'scope') {
    updated.user = '';
    setEmployeeList([]);
  }

  if (name === 'monthYear') {
    updated.startDate = '';
    updated.endDate = '';
  }

  setFilters(updated);
  triggerFilterChange(updated);
};

  const handleDateChange = (name, date) => {
    const updated = {
      ...filters,
      [name]: date || '',
      monthYear: '',
    };

    if (name === 'endDate' && updated.startDate && date && date < updated.startDate) {
      alert('End date must be on or after the start date.');
      return;
    }

    setFilters(updated);
    triggerFilterChange(updated);
  };

  const handleClear = () => {
    setFilters(defaultFilters);
    setScopeList([]);
    setEmployeeList([]);
    triggerFilterChange(defaultFilters);
  };

  const Field = ({ label, children }) => (
    <div className="flex flex-col gap-1 min-w-[160px]">
      <label className="text-xs text-gray-600 font-medium">{label}</label>
      {children}
    </div>
  );

  const inputClass =
    'w-full px-3 py-1.5 text-sm border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white';

  return (
    <div className="w-full bg-white border border-gray-200 shadow-md rounded-xl p-4 overflow-x-auto">
      <div className="flex gap-5 flex-nowrap overflow-x-auto">
        <Field label="Client">
          <select name="client" value={filters.client} onChange={handleChange} className={inputClass}>
            <option value="">Select</option>
            {clientList.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Scope">
          <select name="scope" value={filters.scope} onChange={handleChange} className={inputClass}>
            <option value="">Select</option>
            {scopeList.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="User">
          <select
            name="user"
            value={filters.user}
            onChange={handleChange}
            className={inputClass}
            disabled={!filters.client || !filters.scope}
          >
            <option value="">Select</option>
            {employeeList.map((e) => (
              <option key={e.value} value={e.value}>
                {e.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="From Date">
          <Flatpickr
            value={filters.startDate}
            onChange={([date]) => handleDateChange('startDate', date)}
            options={{ dateFormat: 'm/d/Y' }}
            className={inputClass}
          />
        </Field>

        <Field label="To Date">
          <Flatpickr
            value={filters.endDate}
            onChange={([date]) => handleDateChange('endDate', date)}
            options={{ dateFormat: 'm/d/Y', minDate: filters.startDate || null }}
            className={inputClass}
          />
        </Field>

        <Field label="Month & Year">
          <input type="month" name="monthYear" value={filters.monthYear} onChange={handleChange} className={inputClass} />
        </Field>

        <div className="flex items-end">
          <button
            onClick={handleClear}
            className="px-4 py-2 text-sm font-semibold rounded-full border border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition"
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );
};

export default HorizontalFilterBar;
